/* istanbul ignore file */
import Urn from '@adc/structures/urn'
import seek, { seekTypes } from '@adc/seek'
import fetch from '@adc/fetch'
import logger from '@adc/logger'
import { gql } from '@adc/sdk'
import type {
  CollectionResult,
  CreateOrganization,
  Organization,
  AddOrganizationMemberInput,
  UpdateOrganizationMemberInput,
  UpdateOrganizationInput,
  DomainEventFilters,
  DomainEvents,
  PrivateSellerDocumentUploadInput,
  AddOrganizationConnectionInput,
  UpdateOrganizationConnectionInput,
  EmptyResult,
  EditOrganizationIntegrationInput,
  ResetPasswordMemberInput,
  ShallowTaskWorkflow,
  CreateEvaluationRuleInput,
  EvaluationRule,
  EvaluationRuleKeySchema,
  EvaluationRuleResult,
  Listing,
  TestEvaluationRuleInput,
  CreateOrganizationWorkingWindowInput,
  UpdateOrganizationWorkingWindowInput,
  OrganizationWorkingWindow,
  IngestInput,
  IngestResult,
} from '@adc/sdk/types'
import { match } from 'ts-pattern'
import seekThroughGraph from './seekThroughGraph'

export type MinimalOrganizationForLabel = Pick<
  Organization,
  'name' | 'urn' | 'id'
>

type FetchResult = {
  data: CollectionResult
  errors: Record<string, unknown>[]
}

export type OrganizationFilter = {
  fields: string[]
  values: string[]
}

export type GetOrganizationParams = {
  sortBy?: { field: string; order: 'asc' | 'desc' }[]
  filterBy: OrganizationFilter[]
  size: number
  offset: number
}

export const addMemberToOrganization = (
  input: AddOrganizationMemberInput
): Promise<Organization> => {
  return fetch
    .forAuctionGraph<'addMemberToOrganization', Organization>({
      variables: {
        input,
      },
      query: gql`
        mutation morpheus_addMemberToOrganization(
          $input: AddOrganizationMemberInput!
        ) {
          addMemberToOrganization(input: $input) {
            id
            members {
              id
              groups
              external_organization_accesses {
                id
                name
              }
            }
          }
        }
      `,
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }
      return response.body.data.addMemberToOrganization
    })
}

export const updateMemberInfo = (
  input: UpdateOrganizationMemberInput
): Promise<Organization> => {
  return fetch
    .forAuctionGraph<'updateMemberInfo', Organization>({
      variables: {
        input,
      },
      query: gql`
        mutation morpheus_updateMemberInfo(
          $input: UpdateOrganizationMemberInput!
        ) {
          updateMemberInfo(input: $input) {
            id
            members {
              id
              groups
              external_organization_accesses {
                id
                name
              }
            }
          }
        }
      `,
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }
      return response.body.data.updateMemberInfo
    })
}

export const getOrganizations = (
  _evt: unknown,
  params: GetOrganizationParams
): Promise<FetchResult> => {
  return seekThroughGraph(
    null,
    {
      ...params,
      fields: [
        'id',
        'name',
        'is_trusted',
        'created_date',
        'types',
        'aliases',
        'updated_date',
        'allowed_domains',
        'groups.name',
        'groups.description',
      ],
    },
    seekTypes.ORGANIZATIONS
  ) as Promise<FetchResult>
}

/**
 * Fetches all organizations with only name and urn fields.
 * Handles pagination automatically to retrieve all organizations.
 * Uses a Map keyed by organization ID to deduplicate results.
 * @returns Promise resolving to array of unique organizations with name and urn
 */
// Module-level cache: the org list rarely changes within a session. Cache the
// in-flight promise so repeated page mounts share a single fetch. Reset on
// failure so a later attempt can retry.
//
// Consumers are the org-members admin grid (org filter options + id→name column
// map) and the activity-timeline org filter — each fetches on its own route's
// mount, so this only runs when one of those routes loads. The org /edit
// quick-switcher no longer fetches all orgs; it uses server-side type-ahead.
//
// Uses @adc/seek's `dangerouslyExecuteAllBatched` — page 0 (also the count probe)
// then one multi-root request for the remaining pages — so the full list arrives
// in ≤ 2 round trips instead of ⌈N/250⌉ sequential seeks.
let allMinimalOrganizationsPromise: Promise<
  Array<MinimalOrganizationForLabel>
> | null = null

const fetchAllMinimalOrganizations = async (): Promise<
  Array<MinimalOrganizationForLabel>
> => {
  const organizations = await seek
    .for(seekTypes.ORGANIZATIONS)
    .fields(['name', 'urn', 'id'])
    .operationName('resiFeBtbhome_allOrganizationLabels')
    .dangerouslyExecuteAllBatched<MinimalOrganizationForLabel>()

  // Dedupe by id (mirrors the previous Map-based dedup; offset paging without a
  // stable sort can theoretically repeat a row across pages).
  const organizationsById = new Map<string, MinimalOrganizationForLabel>()
  organizations.forEach(org => organizationsById.set(org.id, org))
  return Array.from(organizationsById.values())
}

export const getAllMinimalOrganizations = (): Promise<
  Array<MinimalOrganizationForLabel>
> => {
  if (!allMinimalOrganizationsPromise) {
    allMinimalOrganizationsPromise = fetchAllMinimalOrganizations().catch(
      error => {
        // Allow a subsequent call to retry after a failed fetch
        allMinimalOrganizationsPromise = null
        throw error
      }
    )
  }
  return allMinimalOrganizationsPromise
}

// Clears the cached org-list promise. Primarily for tests to keep isolation;
// callers that need fresh data (e.g. after creating an org) can also use it.
export const resetMinimalOrganizationsCache = (): void => {
  allMinimalOrganizationsPromise = null
}

export const createOrganization = (
  input: CreateOrganization
): Promise<Organization> => {
  return fetch
    .forAuctionGraph<'createOrganization', Organization>({
      variables: {
        input,
      },
      query: gql`
        mutation morpheus_CreateOrganization($input: CreateOrganization!) {
          createOrganization(input: $input) {
            id
            name
            is_trusted
            types
            aliases
            allowed_domains
            salesforce_account_id
            primary_owner_name
            primary_owner_email
            secondary_owner_name
            secondary_owner_email
            capabilities {
              capability_id
              code
              enabled
            }
            associated_evaluation_rules {
              ${RULES_FRAGMENT}
            }
          }
        }
      `,
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }

      return response.body.data.createOrganization
    })
}
type fetchOrganizationMemebersArgs = {
  seekType: seekTypes
  operationName: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dsl: any // todo: investigate if we can expand the expected dsl type on seek to not need to use any here
  fields: string[]
  fieldsConfig?: Parameters<seek['fields']>[1] // grab the fieldsConfig (param) type that seek.fields() will expect
  sortField: string
  sortDirection: string
  paginationState?: {
    pageSize: number
    offset: number
  }
}
export const fetchOrganizationMembersSeek = ({
  seekType,
  operationName,
  dsl,
  fields,
  fieldsConfig,
  sortField,
  sortDirection,
  paginationState,
}: fetchOrganizationMemebersArgs): Promise<FetchResult> => {
  const seeker = seek
    .for(seekType)
    .operationName(operationName)
    .dsl(dsl)
    .fields(fields, fieldsConfig)
    .sortByField(sortField, sortDirection)
    .size(paginationState?.pageSize ?? 10000)
    .offset(paginationState?.offset ?? 0)

  return seeker.execute().then(({ data, errors }) => {
    const result = {
      data: data.seek,
      errors,
    }
    return result
  })
}

type FetchOrganizationsSeekArgs = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dsl: any
  fields: string[]
  sortField: string
  sortDirection: string
  paginationState?: {
    pageSize: number
    offset: number
  }
}

export const fetchOrganizationsSeek = ({
  dsl,
  fields,
  sortField,
  sortDirection,
  paginationState,
}: FetchOrganizationsSeekArgs): Promise<FetchResult> => {
  const seeker = seek
    .for(seekTypes.ORGANIZATIONS)
    .operationName('resiFeBtbhome_adcSeek_organizations')
    .dsl(dsl)
    .fields(fields)
    .size(paginationState?.pageSize ?? 200)
    .offset(paginationState?.offset ?? 0)

  // Set _sort directly (lowercase order, raw field) to match the proven
  // seekThroughGraph/legacy-grid shape. seek's .sortByField() uppercases the
  // order to 'ASC'/'DESC', which the ORGANIZATIONS backend does not honor.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(seeker as any)._sort = [
    { field: sortField || 'name.keyword', order: sortDirection || 'asc' },
  ]

  return seeker.execute().then(({ data, errors }) => {
    const result = {
      data: data.seek,
      errors,
    }
    return result
  })
}

// TODO: Use sdk type when it´s available
export type OrganizationGroup = {
  id: string
  name: string
  group_type?: string
}

export const fetchOrganizationGroups = (): Promise<string[]> => {
  return fetch
    .forAuctionGraph<'active_groups', string[]>({
      query: gql`
        query morpheus_fetchActiveOrganizationGroups {
          active_groups
        }
      `,
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }
      const groups = response.body.data.active_groups
      if (groups?.some(group => group == null)) {
        logger.warn(
          'active_groups contains unmappable enum value(s); dropping nulls',
          { groups }
        )
      }
      return groups
    })
}

export const updateOrganization = (
  input: UpdateOrganizationInput
): Promise<Organization> => {
  return fetch
    .forAuctionGraph<'updateOrganization', Organization>({
      variables: { input },
      query: gql`
        mutation morpheus_UpdateOrganization($input: UpdateOrganizationInput!) {
          updateOrganization(input: $input) {
            id
            name
            is_trusted
            types
            aliases
            allowed_domains
            salesforce_account_id
            primary_owner_name
            primary_owner_email
            secondary_owner_name
            secondary_owner_email
            capabilities {
              capability_id
              code
              enabled
            }
            groups {
              id
              group_type
            }
            connections {
              ${CONNECTION_FRAGMENT}
            }
            integrations {
              ${INTEGRATION_FRAGMENT}
            }
          }
        }
      `,
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }
      return response.body.data.updateOrganization
    })
}

const INTEGRATION_FRAGMENT = `
  integration_id
  connection_id
  capability_id
  type
  flow_direction
  channel
  filter_dsl
  enabled
  clearance_enabled_states
  allow_cancellation_past_auction
`
const CONNECTION_FRAGMENT = `
  connection_id
  display_name
  channel
  enabled
  api_detail {
    id
    url
    username
    password
    http_method
    auth_method
    soap_action
  }
  email_detail {
    id
    email_address
  }
  sftp_detail {
    id
    hostname
    username
    password
    port
    default_folder_name
  }
`

const RULES_FRAGMENT = `
  evaluation_rule_id
  organization_urn
  rule_key
  rule_type_code
  rule_definition
  version
  source
  created_date
  updated_date
`

export const addOrganizationConnection = (
  input: AddOrganizationConnectionInput
): Promise<Organization> => {
  return fetch
    .forAuctionGraph<'addOrganizationConnection', Organization>({
      variables: { input },
      query: gql`
        mutation morpheus_addOrganizationConnection(
          $input: AddOrganizationConnectionInput!
        ) {
          addOrganizationConnection(input: $input) {
            ${CONNECTION_FRAGMENT}
          }
        }
      `,
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }
      return response.body.data.addOrganizationConnection
    })
}

export const updateOrganizationConnection = (
  input: UpdateOrganizationConnectionInput
): Promise<Organization> => {
  return fetch
    .forAuctionGraph<'updateOrganizationConnection', Organization>({
      variables: { input },
      query: gql`
        mutation morpheus_updateOrganizationConnection(
          $input: UpdateOrganizationConnectionInput!
        ) {
          updateOrganizationConnection(input: $input) {
            ${CONNECTION_FRAGMENT}
          }
        }
      `,
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }
      return response.body.data.updateOrganizationConnection
    })
}

export const deleteOrganizationConnection = (
  connectionId: number
): Promise<EmptyResult> => {
  return fetch
    .forAuctionGraph<'deleteOrganizationConnection', EmptyResult>({
      variables: { connectionId },
      query: gql`
        mutation morpheus_deleteOrganizationConnection($connectionId: Int!) {
          deleteOrganizationConnection(connectionId: $connectionId) {
            __typename
          }
        }
      `,
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }
      return response.body.data.deleteOrganizationConnection
    })
}

export const fetchOrganizationById = (
  organizationId: string | number
): Promise<Organization> => {
  return fetch
    .forAuctionGraph<'organization', Organization>({
      variables: { organizationId: String(organizationId) },
      query: gql`
        query morpheus_selectedOrganization($organizationId: ID!) {
          organization(organizationId: $organizationId) {
            id
            name
            is_trusted
            types
            aliases
            allowed_domains
            salesforce_account_id
            primary_owner_name
            primary_owner_email
            secondary_owner_name
            secondary_owner_email
            capabilities {
              capability_id
              code
              enabled
            }
            connections {
              ${CONNECTION_FRAGMENT}
            }
            integrations {
              ${INTEGRATION_FRAGMENT}
            }
            groups {
              id
              group_type
            }
            mappings {
              id
              organization_id
              is_default_mapping_for_organization
              version
              name
              status
              version_comment
              created_by
              created_date
              updated_by
              updated_date
            }
            associated_evaluation_rules {
              ${RULES_FRAGMENT}
            }
            working_windows {
              ${WORKING_WINDOW_FRAGMENT}
            }
            restrictions(filters: { take: 100 }) {
              content {
                user_restriction_id
                user_id
                action
                user {
                  user_id
                  party_id
                  first_name
                  last_name
                  email
                }
              }
            }
          }
        }
      `,
    })
    .then(response => {
      if (response.body.errors && response.body.errors.length > 0) {
        // If we got partial data, log warnings but don't throw
        if (response.body.data?.organization) {
          console.warn(
            '[Organization] Partial errors in organization query:',
            response.body.errors.map(
              (e: { message: string; path?: string[] }) =>
                `${e.path?.join('.')}: ${e.message}`
            )
          )
          return response.body.data.organization
        }
        // No data at all — throw
        throw response.body.errors[0]
      }
      return response.body.data.organization
    })
}

/**
 * Audit history for an organization, fetched on its own so the heavier
 * `morpheus_selectedOrganization` anchor doesn't carry it. `audit_history` is
 * only rendered on the `/organizations/:id/history` route, so co-fetching it
 * with the org on `/edit` (where nothing reads it) was dead weight — and it
 * fans out to the audit backend when selected. Consumed by
 * `getOrganizationHistory` in providers/auditHistory.js.
 */
export const fetchOrganizationAuditHistory = (
  organizationId: string | number
): Promise<Pick<Organization, 'audit_history'>> => {
  return fetch
    .forAuctionGraph<'organization', Pick<Organization, 'audit_history'>>({
      variables: { organizationId: String(organizationId) },
      query: gql`
        query morpheus_organizationAuditHistory($organizationId: ID!) {
          organization(organizationId: $organizationId) {
            audit_history {
              updated_by
              updated_date
              delta {
                added
                modified
                removed
              }
            }
          }
        }
      `,
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }
      return response.body.data.organization
    })
}

export type IntegrationPayload = Partial<EditOrganizationIntegrationInput>

export const createIntegration = (input: IntegrationPayload) => {
  return fetch
    .forAuctionGraph<'addOrganizationIntegration', Organization>({
      variables: { input },
      query: gql`
        mutation morpheus_addOrganizationIntegration(
          $input: AddOrganizationIntegrationInput!
        ) {
          addOrganizationIntegration(input: $input) {
            ${INTEGRATION_FRAGMENT}
          }
        }
      `,
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }
      return response.body.data.addOrganizationIntegration
    })
}

export const updateIntegration = (input: IntegrationPayload) => {
  return fetch
    .forAuctionGraph<'updateOrganizationIntegration', Organization>({
      variables: { input },
      query: gql`
        mutation morpheus_updateOrganizationIntegration(
          $input: EditOrganizationIntegrationInput!
        ) {
          updateOrganizationIntegration(input: $input) {
            ${INTEGRATION_FRAGMENT}
          }
        }
      `,
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }
      return response.body.data.updateOrganizationIntegration
    })
}

export const deleteOrganizationIntegration = (
  integrationId: number
): Promise<EmptyResult> => {
  return fetch
    .forAuctionGraph<'deleteOrganizationIntegration', EmptyResult>({
      variables: { integrationId },
      query: gql`
        mutation morpheus_deleteOrganizationIntegration($integrationId: Int!) {
          deleteOrganizationIntegration(integrationId: $integrationId) {
            __typename
          }
        }
      `,
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }
      return response.body.data.deleteOrganizationIntegration
    })
}

export const fetchDomainEventsByOrganization = (
  urns: string[],
  filters: DomainEventFilters
): Promise<DomainEvents> => {
  return fetch
    .forAuctionGraph<'domain_events', DomainEvents>({
      variables: { urns, filters },
      query: gql`
        query morpheus_fetchDomainEventsByOrganization(
          $urns: [URN!]!
          $filters: DomainEventFilters!
        ) {
          domain_events(urns: $urns, filters: $filters) {
            events {
              id
              source
              subject
              type
              data
              user_id
              party_id
              service
              time
            }
          }
        }
      `,
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }
      return response.body.data.domain_events
    })
}

export const validatePrivateSellerFile = (
  input: PrivateSellerDocumentUploadInput
) => {
  return fetch
    .forAuctionGraph<'ingest', IngestResult>({
      query: gql`
        mutation morpheus_validatePrivateSellerFile(
          $input: PrivateSellerDocumentUploadInput!
        ) {
          ingest(
            input: {
              type: "PRIVATE_SELLER_FILE"
              operation: VALIDATE
              metadata: { private_seller_file: $input }
            }
          ) {
            private_seller_validation_response {
              summary {
                record_count
                success_count
                warning_count
                error_count
                warnings
                errors
              }
              row_validations {
                row_number
                errors
                warnings
              }
            }
          }
        }
      `,
      variables: { input },
    })
    .then(res => {
      if (res.body.errors && res.body.errors[0]) {
        throw res.body.errors[0]
      }
      return res.body
    })
}

export const ingest = (input: IngestInput) => {
  return fetch
    .forAuctionGraph<'ingest', IngestResult>({
      query: gql`
        mutation morpheus_ingest($input: IngestInput!) {
          ingest(input: $input) {
            ingestion_id
            intake_batch_id
            file_name
            organization_id
          }
        }
      `,
      variables: { input },
    })
    .then(res => {
      if (res.body.errors && res.body.errors[0]) {
        throw res.body.errors[0]
      }
      return res.body
    })
}

export const resetPasswordMember = (
  input: ResetPasswordMemberInput
): Promise<EmptyResult> => {
  return fetch
    .forAuctionGraph<'resetPasswordMember', EmptyResult>({
      variables: {
        input,
      },
      query: gql`
        mutation morpheus_resetPasswordMember(
          $input: ResetPasswordMemberInput!
        ) {
          resetPasswordMember(input: $input) {
            __typename
          }
        }
      `,
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }
      return response.body.data.resetPasswordMember
    })
}

/**
 * Fetches the client onboarding workflow for an organization.
 * Uses the root `workflow(urn:)` query with an organization URN.
 *
 * NOTE: Short-term implementation — the graph layer computes task statuses
 * from ms-uaa org data. Will be replaced with ms-workflow integration once
 * non-listing workflow support lands (see resi-ms-workflow PR #109).
 */
export const fetchOnboardingWorkflow = (
  organizationId: string | number
): Promise<ShallowTaskWorkflow | null> => {
  const urn = new Urn('organization', organizationId).toString()

  return fetch
    .forAuctionGraph<'workflow', ShallowTaskWorkflow | null>({
      variables: { urn },
      query: gql`
        query morpheus_OrganizationOnboardingWorkflow($urn: URN!) {
          workflow(urn: $urn) {
            current_status_label
            task_groups {
              code
              label
              description
              label_tooltip
              description_tooltip
              expectation
              status
              tasks {
                code
                label
                expectation
                status
                last_updated_date
                completed_date
              }
            }
          }
        }
      `,
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }
      return response.body.data.workflow
    })
}

/**
 * Search organizations by name. Always searches by name (3+ chars required at call site).
 * If input is numeric, also fetches the exact-match org by ID and prepends it to results.
 */
export const searchOrganizations = async (
  searchText: string
): Promise<Array<{ id: string; name: string }>> => {
  const isNumeric = /^\d+$/.test(searchText)

  // Always do name search
  const nameSearchPromise = fetch.forAuctionGraph<
    'organizations',
    { content: Array<{ id: string; name: string }>; total_count: number }
  >({
    variables: {
      filters: { take: 20 },
      searchCriteria: { name: searchText },
    },
    query: gql`
      query morpheus_searchOrganizations(
        $filters: FilterableCollectionFilters
        $searchCriteria: OrganizationFilter
      ) {
        organizations(filters: $filters, searchCriteria: $searchCriteria) {
          content {
            id
            name
          }
          total_count
        }
      }
    `,
  })

  // If numeric, also fetch exact ID match
  const idMatchPromise = isNumeric
    ? fetchOrganizationById(searchText).catch(() => null)
    : Promise.resolve(null)

  const [nameResponse, idMatch] = await Promise.all([
    nameSearchPromise,
    idMatchPromise,
  ])

  if (nameResponse.body.errors && nameResponse.body.errors[0]) {
    throw nameResponse.body.errors[0]
  }

  const nameResults = nameResponse.body.data.organizations.content || []

  // Prepend exact ID match if found and not already in name results
  if (
    idMatch &&
    !nameResults.some(org => String(org.id) === String(idMatch.id))
  ) {
    return [
      {
        id: String(idMatch.id),
        name: idMatch.name || `Organization ${idMatch.id}`,
      },
      ...nameResults,
    ]
  }

  // If ID match is already in name results, move it to top
  if (idMatch) {
    const withoutMatch = nameResults.filter(
      org => String(org.id) !== String(idMatch.id)
    )
    const match = nameResults.find(org => String(org.id) === String(idMatch.id))
    if (match) return [match, ...withoutMatch]
  }

  return nameResults
}
// ── Evaluation Rules ─────────────────────────────────────────────────

const EVALUATE_EVALUATION_RULE_MUTATION = gql`
  mutation testEvaluationRule($input: TestEvaluationRuleInput!) {
    testEvaluationRule(input: $input) {
      matched
      output
    }
  }
`

export const testEvaluationRule = (
  input: TestEvaluationRuleInput
): Promise<EvaluationRuleResult> =>
  fetch
    .forAuctionGraph<'testEvaluationRule', EvaluationRuleResult>({
      query: EVALUATE_EVALUATION_RULE_MUTATION,
      variables: { input },
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw new Error(response.body.errors[0].message)
      }

      return response.body.data.testEvaluationRule
    })

// ── Evaluation Rules CRUD ────────────────────────────────────────────

const CREATE_EVALUATION_RULE_MUTATION = gql`
  mutation createEvaluationRule($input: CreateEvaluationRuleInput!) {
    createEvaluationRule(input: $input) {
      evaluation_rule_id
      organization_urn
      rule_key
      rule_type_code
      rule_definition
      version
      source
      created_date
      updated_date
    }
  }
`

export const createEvaluationRule = (
  input: CreateEvaluationRuleInput
): Promise<EvaluationRule> =>
  fetch
    .forAuctionGraph<'createEvaluationRule', EvaluationRule>({
      query: CREATE_EVALUATION_RULE_MUTATION,
      variables: { input },
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw new Error(response.body.errors[0].message)
      }
      return response.body.data.createEvaluationRule
    })

const UPDATE_EVALUATION_RULE_MUTATION = gql`
  mutation updateEvaluationRule($id: ID!, $input: UpdateEvaluationRuleInput!) {
    updateEvaluationRule(id: $id, input: $input) {
      evaluation_rule_id
      organization_urn
      rule_key
      rule_type_code
      rule_definition
      version
      source
      created_date
      updated_date
    }
  }
`

export const updateEvaluationRule = (
  id: string,
  input: CreateEvaluationRuleInput
): Promise<EvaluationRule> =>
  fetch
    .forAuctionGraph<'updateEvaluationRule', EvaluationRule>({
      query: UPDATE_EVALUATION_RULE_MUTATION,
      variables: { id, input },
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw new Error(response.body.errors[0].message)
      }
      return response.body.data.updateEvaluationRule
    })

const DELETE_EVALUATION_RULE_MUTATION = gql`
  mutation deleteEvaluationRule($id: ID!) {
    deleteEvaluationRule(id: $id)
  }
`

export const deleteEvaluationRule = (id: string): Promise<boolean> =>
  fetch
    .forAuctionGraph<'deleteEvaluationRule', boolean>({
      query: DELETE_EVALUATION_RULE_MUTATION,
      variables: { id },
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw new Error(response.body.errors[0].message)
      }
      return response.body.data.deleteEvaluationRule
    })

// ── Fetch listing for rule evaluation context with the fields that we currently evaluate. Wi will be replacing with a different set shortly ───────────────────

/**
 * Builds a GraphQL query dynamically from source paths.
 * Converts dot-paths like 'listing.primary_property.summary.address.country_primary_subdivision'
 * into nested selection sets.
 */
function buildListingQueryFromPaths(sourcePaths: string[]): string {
  // Build a nested tree from all paths
  const tree: Record<string, unknown> = {}
  for (const path of sourcePaths) {
    // Remove 'listing.' prefix since we're already inside listing { }
    const segments = path.startsWith('listing.')
      ? path.substring('listing.'.length).split('.')
      : path.split('.')
    let node: Record<string, unknown> = tree
    for (const segment of segments) {
      if (!node[segment]) node[segment] = {}
      node = node[segment] as Record<string, unknown>
    }
  }

  // Convert tree to GQL selection string
  function treeToGql(obj: Record<string, unknown>, indent = '      '): string {
    const entries = Object.entries(obj)
    return entries
      .map(([key, value]) => {
        const children = Object.keys(value as Record<string, unknown>)
        if (children.length === 0) {
          return `${indent}${key}`
        }
        return `${indent}${key} {\n${treeToGql(
          value as Record<string, unknown>,
          indent + '  '
        )}\n${indent}}`
      })
      .join('\n')
  }

  return `
    query listingForEvaluation($id: ID!) {
      listing(listingId: $id) {
        listing_id
${treeToGql(tree)}
      }
    }
  `
}

export const fetchListingForEvaluation = (
  listingId: string,
  sourcePaths: string[]
): Promise<Listing> => {
  const query = buildListingQueryFromPaths(sourcePaths)
  return fetch
    .forAuctionGraph<'listing', Listing>({
      query,
      variables: { id: listingId },
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw new Error(response.body.errors[0].message)
      }
      return response.body.data.listing
    })
}

// ── Evaluation Rule Keys (Schema Discovery) ──────────────────────────

const EVALUATION_RULE_KEYS_QUERY = gql`
  query evaluationRuleKeys {
    evaluation_rule_keys {
      key
      type_code
      output_type
      context_fields {
        field
        type
        source_path
        description
      }
    }
  }
`

const getSourcePath = (field: string): string | null =>
  match(field)
    .with(
      'STATE',
      () =>
        'listing.primary_property.summary.address.country_primary_subdivision'
    )
    .with('PRODUCT_TYPE', () => 'listing.listing_configuration.product_type')
    .with('RUN_NUMBER', () => 'listing.listing_configuration.run_number')
    .with('SELLER_CODE', () => 'listing.listing_configuration.mlh_seller_code')
    .with(
      'SELLER_PROGRAM',
      () => 'listing.program_configuration.seller_program'
    )
    .with(
      'ONLINE_SEGMENT_TYPE',
      () => 'listing.online_auction_segment.segment_type'
    )
    .with('IS_ONLINE', () => 'listing.auction.is_online')
    .with(
      'ONLINE_SEGMENT_STATUS',
      () => 'listing.online_auction_segment.segment_status'
    )
    .with(
      'PARENT_SELLER',
      () => 'listing.program_configuration.parent_seller_name'
    )
    .with(
      'COUNTY',
      () => 'listing.seller_property.country_secondary_subdivision'
    )
    .with(
      'OCCUPANCY_STATUS',
      () => 'listing.listing_configuration.occupancy_status'
    )
    .with(
      'IS_FIRST_LOOK',
      () => 'listing.listing_configuration.is_first_look_enabled'
    )
    .with('VALUATION', () => 'listing.valuation.seller_current_value_amount')
    .otherwise(() => null)

const mapSourcePaths = (
  evaluationRuleKeys: EvaluationRuleKeySchema[]
): EvaluationRuleKeySchema[] =>
  evaluationRuleKeys.map(ruleKey => ({
    ...ruleKey,
    context_fields: ruleKey.context_fields.map(field => ({
      ...field,
      source_path: getSourcePath(field.field) ?? field.source_path,
    })),
  }))

export const fetchEvaluationRuleKeys = (): Promise<EvaluationRuleKeySchema[]> =>
  fetch
    .forAuctionGraph<'evaluation_rule_keys', EvaluationRuleKeySchema[]>({
      query: EVALUATION_RULE_KEYS_QUERY,
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw new Error(response.body.errors[0].message)
      }
      return mapSourcePaths(response.body.data.evaluation_rule_keys)
    })

// ── Organization Working Windows CRUD ────────────────────────────────────────

const WORKING_WINDOW_FRAGMENT = `
  id
  window_type
  day_of_week
  window_start_time
  window_end_time
  timezone
  label
`

export const createOrganizationWorkingWindow = (
  input: CreateOrganizationWorkingWindowInput
): Promise<OrganizationWorkingWindow> =>
  fetch
    .forAuctionGraph<
      'createOrganizationWorkingWindow',
      OrganizationWorkingWindow
    >({
      variables: { input },
      query: gql`
        mutation morpheus_createOrganizationWorkingWindow(
          $input: CreateOrganizationWorkingWindowInput!
        ) {
          createOrganizationWorkingWindow(input: $input) {
            ${WORKING_WINDOW_FRAGMENT}
          }
        }
      `,
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw new Error(response.body.errors[0].message)
      }
      return response.body.data.createOrganizationWorkingWindow
    })

export const updateOrganizationWorkingWindow = (
  workingWindowId: string,
  input: UpdateOrganizationWorkingWindowInput
): Promise<OrganizationWorkingWindow> =>
  fetch
    .forAuctionGraph<
      'updateOrganizationWorkingWindow',
      OrganizationWorkingWindow
    >({
      variables: { id: workingWindowId, input },
      query: gql`
        mutation morpheus_updateOrganizationWorkingWindow(
          $id: ID!
          $input: UpdateOrganizationWorkingWindowInput!
        ) {
          updateOrganizationWorkingWindow(id: $id, input: $input) {
            ${WORKING_WINDOW_FRAGMENT}
          }
        }
      `,
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw new Error(response.body.errors[0].message)
      }
      return response.body.data.updateOrganizationWorkingWindow
    })

export const deleteOrganizationWorkingWindow = (
  id: string,
  organizationId: string
): Promise<boolean> =>
  fetch
    .forAuctionGraph<'deleteOrganizationWorkingWindow', boolean>({
      variables: { id, organizationId },
      query: gql`
        mutation morpheus_deleteOrganizationWorkingWindow(
          $id: ID!
          $organizationId: ID!
        ) {
          deleteOrganizationWorkingWindow(
            id: $id
            organizationId: $organizationId
          ) {
            __typename
          }
        }
      `,
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw new Error(response.body.errors[0].message)
      }
      return response.body.data.deleteOrganizationWorkingWindow
    })
